import React, { useCallback, useRef } from "react";
import { useField } from "formik";
import { useDropzone } from "react-dropzone";
import {
  Image as ImageIcon,
  ImageIcon as BrowseIcon,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import SectionCard from "../ui/SectionCard";
import {
  appendProductImageToFormik,
  pickPreviewUrl,
} from "../../../../../Common/ImageUpload/handleProductImageUpload";

const MAX_IMAGES = 10;

const MediaSection = () => {
  const [, meta, helpers] = useField("images");
  const images = Array.isArray(meta.value) ? meta.value : [];
  const invalid = meta.touched && !!meta.error;

  const cover = images[0];
  const gallery = images.slice(1);

  const browseInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const dragIndex = useRef(null);
  const overIndex = useRef(null);

  const addFiles = useCallback(
    async (files) => {
      const remaining = Math.max(0, MAX_IMAGES - images.length);
      const accepted = Array.from(files).slice(0, remaining);
      const state = { list: images.slice() };
      const shim = {
        get values() {
          return { images: state.list };
        },
        setFieldValue: (_n, val) => {
          state.list = val;
        },
      };
      for (const file of accepted) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await appendProductImageToFormik(file, shim, "images");
        } catch {
          /* upload helper surfaces its own error */
        }
      }
      helpers.setValue(state.list);
      helpers.setTouched(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [images],
  );

  const replaceAt = async (index, file) => {
    try {
      let uploaded = null;
      const captureShim = {
        values: { images: [] },
        setFieldValue: (_n, val) => {
          uploaded = val[val.length - 1];
        },
      };
      await appendProductImageToFormik(file, captureShim, "images");
      if (!uploaded) return;
      const next = images.slice();
      next[index] = uploaded;
      helpers.setValue(next);
      helpers.setTouched(true);
    } catch {
      /* upload helper surfaces its own error */
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    onDrop: addFiles,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const removeAt = (index) => {
    const next = images.slice();
    next.splice(index, 1);
    helpers.setValue(next);
  };

  const promoteToCover = (index) => {
    if (index === 0) return;
    const next = images.slice();
    [next[0], next[index]] = [next[index], next[0]];
    helpers.setValue(next);
  };

  // Drag-reorder inside the gallery strip (indices 1..n); cover stays at 0.
  const handleDragStart = (absIndex) => {
    dragIndex.current = absIndex;
  };
  const handleDragEnter = (absIndex) => {
    overIndex.current = absIndex;
  };
  const handleDragEnd = () => {
    const from = dragIndex.current;
    const to = overIndex.current;
    dragIndex.current = null;
    overIndex.current = null;
    if (from === null || to === null || from === to) return;
    if (from < 1 || to < 1) return;
    const next = images.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    helpers.setValue(next);
  };

  const reachedLimit = images.length >= MAX_IMAGES;

  const openBrowse = () => browseInputRef.current?.click();
  const openReplace = () => replaceInputRef.current?.click();

  return (
    <SectionCard
      icon={<ImageIcon size={16} />}
      title="Upload Product Image"
      subtitle="First image is the cover. Click a gallery image to promote it."
      id="media"
      trailing={
        <span>
          {images.length}/{MAX_IMAGES}
        </span>
      }
    >
      <div className="pf-media__label">Product Image</div>

      <div
        {...getRootProps({
          className: `pf-media__hero ${
            isDragActive ? "is-drag" : ""
          } ${invalid ? "is-invalid" : ""}`,
          "aria-invalid": invalid,
        })}
      >
        <input {...getInputProps()} />

        {cover ? (
          <img
            className="pf-media__hero-img"
            src={pickPreviewUrl(cover, "800w")}
            alt="Product cover"
          />
        ) : (
          <div className="pf-media__hero-empty">
            <BrowseIcon size={32} />
            <div className="pf-media__hero-empty-title">
              Drop an image here or use Browse
            </div>
            <div className="pf-media__hero-empty-hint">
              Uploads are converted to WebP and resized on the server.
            </div>
          </div>
        )}

        <button
          type="button"
          className="pf-media__hero-btn pf-media__hero-btn--left"
          onClick={openBrowse}
          disabled={reachedLimit}
          aria-label="Browse for images"
        >
          <BrowseIcon size={16} />
          <span>Browse</span>
        </button>

        {cover ? (
          <button
            type="button"
            className="pf-media__hero-btn pf-media__hero-btn--right"
            onClick={openReplace}
            aria-label="Replace cover image"
          >
            <RefreshCw size={16} />
            <span>Replace</span>
          </button>
        ) : null}
      </div>

      <input
        ref={browseInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && cover) replaceAt(0, file);
          e.target.value = "";
        }}
      />

      {gallery.length > 0 || !reachedLimit ? (
        <div className="pf-media__strip">
          {gallery.map((img, i) => {
            const absIndex = i + 1;
            return (
              <button
                type="button"
                key={img?.id ?? `local-${absIndex}`}
                className="pf-media__thumb"
                onClick={() => promoteToCover(absIndex)}
                draggable
                onDragStart={() => handleDragStart(absIndex)}
                onDragEnter={() => handleDragEnter(absIndex)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                aria-label={`Promote image ${absIndex + 1} to cover`}
              >
                <span
                  className="pf-media__thumb-remove"
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove image ${absIndex + 1}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(absIndex);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      removeAt(absIndex);
                    }
                  }}
                >
                  <X size={12} />
                </span>
                <img
                  src={pickPreviewUrl(img, "400w")}
                  alt={`Gallery ${absIndex}`}
                  loading="lazy"
                />
              </button>
            );
          })}

          {!reachedLimit ? (
            <button
              type="button"
              className="pf-media__add"
              onClick={openBrowse}
              aria-label="Add image"
            >
              <span className="pf-media__add-icon">
                <Plus size={14} />
              </span>
              <span>Add Image</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {invalid ? (
        <div className="pf-field__error" style={{ marginTop: 10 }}>
          {meta.error}
        </div>
      ) : null}
    </SectionCard>
  );
};

export default React.memo(MediaSection);
